/**
 * Carteira de Trabalho — linhas compactas estilo Jira / Linear (PR3).
 */
import React from 'react';
import { CheckCircle2, Pause, Timer } from 'lucide-react';
import type { TaskListItem } from '../../shared/types/task';
import { formatRelativeTime, formatStatusLabel, resolveUrgency } from '../utils/taskDisplayUtils';
import { formatTaskSlaDetail, resolveOperationalPriority } from '../utils/executionCenterUtils';
import { deskPriorityView } from '../../../components/ops/exceptionLoop';
import { EnterpriseQueueSkeleton } from './EnterpriseQueueSkeleton';

type OriginKind = 'workflow' | 'action' | 'problem';

function getOrigin(task: TaskListItem): { kind: OriginKind; short: string; label: string } {
  const meta = (task.metadata ?? {}) as Record<string, unknown>;
  const src = (meta.source as string | undefined) ?? (meta.workItemSource as string | undefined);
  if (src === 'action') return { kind: 'action', short: 'AC', label: 'Ação' };
  if (src === 'problem') return { kind: 'problem', short: 'PRB', label: 'Problema' };
  return { kind: 'workflow', short: 'WF', label: 'Workflow' };
}

const ORIGIN_BADGE: Record<OriginKind, string> = {
  workflow: 'bg-blue-50 text-blue-700 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:ring-blue-900',
  action: 'bg-violet-50 text-violet-700 ring-violet-100 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-900',
  problem: 'bg-amber-50 text-amber-800 ring-amber-100 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900',
};

function priorityBarClass(opLabel: string): string {
  if (opLabel === 'Crítica') return 'bg-red-500';
  if (opLabel === 'Alta') return 'bg-amber-500';
  if (opLabel === 'Média') return 'bg-blue-400';
  return 'bg-slate-300 dark:bg-slate-600';
}

function resolveRowSignals(task: TaskListItem): {
  isBlocked: boolean;
  idleLabel: string | null;
  progressPct: number | null;
} {
  const isBlocked = task.status === 'paused' || task.status === 'waiting';
  const refMs = new Date(task.startedAt ?? task.updatedAt ?? task.createdAt).getTime();
  const idleHours = (Date.now() - refMs) / 3_600_000;
  const idleLabel =
    task.status === 'in_progress' || task.status === 'paused'
      ? idleHours >= 2
        ? formatRelativeTime(task.updatedAt ?? task.createdAt)
        : null
      : idleHours >= 8
        ? formatRelativeTime(task.createdAt)
        : null;

  const meta = (task.metadata ?? {}) as Record<string, unknown>;
  const checklist = Array.isArray(meta.checklist) ? meta.checklist as Array<{ id: string }> : [];
  const checklistData = (meta.checklistData as Record<string, boolean> | undefined) ?? {};
  const total = checklist.length;
  const done = total > 0 ? checklist.filter((c) => checklistData[c.id]).length : 0;
  const progressPct = total > 0 ? Math.round((done / total) * 100) : null;

  return { isBlocked, idleLabel, progressPct };
}

function buildTooltip(task: TaskListItem): string {
  const origin = getOrigin(task);
  const signals = resolveRowSignals(task);
  const code = task.referenceCode ?? task.instanceId.slice(0, 8).toUpperCase();
  const parts = [
    origin.label,
    task.stageName ?? task.instanceTitle,
    `#${code}`,
    formatTaskSlaDetail(task),
    formatStatusLabel(task.status),
  ];
  if (signals.isBlocked) parts.push('Bloqueada');
  if (signals.idleLabel) parts.push(`Parada ${signals.idleLabel}`);
  if (signals.progressPct != null) parts.push(`Progresso ${signals.progressPct}%`);
  const meta = (task.metadata ?? {}) as Record<string, unknown>;
  if (origin.kind === 'action') {
    const priorityView = deskPriorityView(
      (meta.managerial_priority as string | null) ?? task.priority,
      (meta.operational_signal as string | null) ?? null,
    );
    parts.push(`Prioridade gerencial: ${priorityView.managerialLabel}`);
    if (priorityView.operationalSignal) parts.push(`Sinal operacional: ${priorityView.operationalSignal}`);
    if (meta.problem_id) parts.push(`Problema ${String(meta.problem_id).slice(0, 8)}`);
  }
  return parts.join(' · ');
}

interface InboxQueueRailProps {
  tasks: TaskListItem[];
  selectedTaskId: string | null;
  onSelect: (taskId: string) => void;
  isLoading?: boolean;
  emptyHint?: string;
  embedded?: boolean;
}

export const InboxQueueRail: React.FC<InboxQueueRailProps> = ({
  tasks,
  selectedTaskId,
  onSelect,
  isLoading,
  emptyHint,
  embedded = false,
}) => (
  <div className="flex h-full min-h-0 flex-col bg-white dark:bg-[var(--bg-elevated)]">
    {!embedded && (
      <div className="flex shrink-0 items-center justify-between border-b border-slate-200/80 px-2 py-1 dark:border-[var(--border-default)]">
        <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-slate-500">Carteira</span>
        <span className="font-mono text-[9px] font-bold tabular-nums text-slate-600 dark:text-slate-300">{tasks.length}</span>
      </div>
    )}

    {embedded && tasks.length > 0 && (
      <div className="flex shrink-0 items-center gap-2 border-b border-slate-100/80 px-2 py-0.5 text-[7px] font-bold uppercase tracking-wider text-slate-400 dark:border-slate-800">
        <span className="w-[30px]">Tipo</span>
        <span className="flex-1">Demanda</span>
        <span className="w-[44px] text-right">SLA</span>
        <span className="w-[32px] text-right">Status</span>
      </div>
    )}

    <div className="min-h-0 flex-1 overflow-y-auto">
      {isLoading ? (
        <EnterpriseQueueSkeleton rows={8} />
      ) : tasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-1 px-2 py-8 text-center">
          <CheckCircle2 size={14} className="text-emerald-500" />
          <p className="text-[10px] font-medium text-slate-500">{emptyHint ?? 'Carteira vazia'}</p>
        </div>
      ) : (
        <ul>
          {tasks.map((task) => {
            const isSelected = task.id === selectedTaskId;
            const priority = resolveOperationalPriority(task);
            const origin = getOrigin(task);
            const barLabel = origin.kind === 'action'
              ? deskPriorityView(
                ((task.metadata ?? {}) as Record<string, unknown>).managerial_priority as string | null ?? task.priority,
                null,
              ).managerialLabel
              : priority.operationalLabel;
            const urgency = resolveUrgency(task);
            const signals = resolveRowSignals(task);
            const slaShort = urgency.level === 'overdue' ? urgency.detail : formatTaskSlaDetail(task);
            const statusShort =
              task.status === 'pending' && task.isRoleQueue ? 'Fila'
              : task.status === 'pending' ? 'Pend.'
              : task.status === 'paused' ? 'Pausa'
              : formatStatusLabel(task.status).replace('Em execução', 'Exec.');

            return (
              <li key={task.id}>
                <button
                  type="button"
                  onClick={() => onSelect(task.id)}
                  title={buildTooltip(task)}
                  className={`group relative flex w-full items-center gap-1 border-b border-slate-100/80 px-2 py-[7px] text-left transition-colors dark:border-slate-800/60 ${
                    isSelected
                      ? 'bg-blue-50/95 ring-inset ring-1 ring-blue-200/80 dark:bg-blue-950/30 dark:ring-blue-800/50'
                      : signals.isBlocked
                        ? 'bg-amber-50/40 hover:bg-amber-50/70 dark:bg-amber-950/10'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-900/40'
                  }`}
                >
                  <span
                    className={`absolute left-0 top-0 h-full w-[2px] ${priorityBarClass(barLabel)}`}
                    aria-hidden
                  />
                  <span
                    title={origin.label}
                    className={`inline-flex w-[30px] shrink-0 items-center justify-center rounded px-0.5 text-center font-mono text-[8px] font-bold leading-4 ring-1 ring-inset ${ORIGIN_BADGE[origin.kind]}`}
                  >
                    {origin.short}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[11px] font-medium leading-none text-slate-800 dark:text-slate-100">
                    {task.stageName ?? task.instanceTitle}
                  </span>
                  <span className="flex shrink-0 items-center gap-0.5">
                    {signals.isBlocked && (
                      <Pause size={9} className="text-amber-600 dark:text-amber-400" aria-label="Bloqueada" />
                    )}
                    {signals.idleLabel && (
                      <Timer size={9} className="text-slate-400" aria-label={`Parada ${signals.idleLabel}`} />
                    )}
                    {signals.progressPct != null && signals.progressPct > 0 && signals.progressPct < 100 && (
                      <span className="font-mono text-[7px] font-bold tabular-nums text-blue-600 dark:text-blue-400">
                        {signals.progressPct}%
                      </span>
                    )}
                  </span>
                  <span
                    className={`w-[44px] shrink-0 truncate text-right font-mono text-[8px] tabular-nums leading-none ${
                      urgency.level === 'overdue' ? 'font-semibold text-red-600 dark:text-red-400' : 'text-slate-400'
                    }`}
                  >
                    {slaShort}
                  </span>
                  <span className={`w-[32px] shrink-0 truncate text-right text-[8px] font-medium uppercase leading-none ${
                    signals.isBlocked ? 'text-amber-600' : 'text-slate-400'
                  }`}>
                    {statusShort}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  </div>
);

export default InboxQueueRail;
